import { createRoute, Outlet, redirect } from '@tanstack/react-router';

import { queryClient, queryKeys } from '@web/core/query';
import { meRepository } from '@web/data/repositories';

import { protectedRoute } from './root_routes';

/**
 * Org-scoped subtree. Loader resolves the slug into the canonical
 * `{ id, slug, name }` tuple and prefetches the caller's permissions for
 * this org. Descendants read the org via `useOrgContext` and the
 * permissions via `useAuthz` — both reuse the cache primed here, so the
 * first render after navigation has data resolved (no nav/button flicker).
 */
export const orgRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'orgs/$orgSlug',
  component: () => <Outlet />,
  loader: async ({ params }) => {
    const me = await queryClient.ensureQueryData({
      queryKey: queryKeys.me.all,
      queryFn: () => meRepository.getMe(),
    });
    const org = me.orgs.find((o) => o.slug === params.orgSlug);
    if (!org) {
      // Stale bookmark / lost membership — bounce through the index to land
      // on the user's current first org.
      return redirect({ throw: true, to: '/' }) as never;
    }
    const perms = await queryClient.ensureQueryData({
      queryKey: queryKeys.me.permissions(org.id),
      queryFn: () => meRepository.getMyPermissions(org.id),
    });
    if (!perms.permissions.includes('accounts:access')) {
      return redirect({ throw: true, to: '/' }) as never;
    }
    return { orgId: org.id, orgSlug: org.slug, orgName: org.name };
  },
});
